import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { NEWS_CATEGORIES } from "@/mocks/types";
import { newsList } from "@/mocks/news";

const PAGE_SIZE = 5;
const SORT_OPTIONS = [
  { key: "newest", label: "最新发布", icon: "ri-time-line" },
  { key: "hot", label: "最多阅读", icon: "ri-fire-line" },
];

export default function NewsList() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") ?? "all";
  const sort = params.get("sort") ?? "newest";
  const page = Math.max(1, Number(params.get("page") ?? 1));

  const [filterOpen, setFilterOpen] = useState(false);

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "all" || value === "newest" || value === "1") next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    let list = category === "all" ? newsList : newsList.filter((n) => n.category === category);
    list = [...list].sort((a, b) => {
      if (sort === "hot") return b.readCount - a.readCount;
      return a.publishedAt < b.publishedAt ? 1 : -1;
    });
    return list;
  }, [category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const activeCategory = NEWS_CATEGORIES.find((c) => c.key === category) ?? NEWS_CATEGORIES[0];

  const reset = () => setParams(new URLSearchParams(), { replace: true });

  const FilterBar = (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-[11px] tracking-[0.16em] uppercase text-foreground-400 font-label mb-2">资讯类型</p>
        <div className="flex flex-wrap gap-2">
          {NEWS_CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => {
                update({ category: c.key, page: "1" });
                setFilterOpen(false);
              }}
              className={`px-3 md:px-3.5 h-9 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                category === c.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[11px] tracking-[0.16em] uppercase text-foreground-400 font-label mb-2">排序方式</p>
        <div className="flex gap-2">
          {SORT_OPTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                update({ sort: s.key, page: "1" });
                setFilterOpen(false);
              }}
              className={`inline-flex items-center gap-1.5 px-3 md:px-3.5 h-9 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                sort === s.key
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              <i className={`${s.icon} text-sm`} />
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <SiteLayout
      title="动漫资讯 - 91动漫"
      description="91动漫资讯频道汇总新番情报、行业动态、剧场版消息、线下活动与声优动态，按类型与时间筛选，快速掌握国内外动漫最新动态。"
      keywords="动漫资讯,新番情报,动漫行业动态,剧场版,91动漫"
    >
      <section className="w-full bg-foreground-950">
        <div className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-8 md:pb-10">
          <div className="text-background-300">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫资讯" }]} />
          </div>
          <h1 className="mt-4 font-heading font-bold text-background-50 text-2xl md:text-4xl tracking-tight">
            动漫资讯
          </h1>
          <p className="mt-3 text-sm md:text-base text-background-300 leading-relaxed max-w-2xl">
            汇总新番情报、行业动态、剧场版消息与线下活动。支持按资讯类型筛选与按发布时间、阅读量排序，帮助你快速掌握国内外动漫最新动态。
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="px-2.5 py-1 rounded-md bg-primary-500/15 text-primary-300 text-[11px] font-label">
              当前分类：{activeCategory.label}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/10 text-background-200 text-[11px] font-label">
              共 {filtered.length} 条资讯
            </span>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 lg:gap-10">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-100 p-5">
              {FilterBar}
            </div>
          </aside>

          <div className="min-w-0">
            <div className="lg:hidden mb-4">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="w-full h-11 rounded-md border border-background-300 bg-background-50 flex items-center justify-between px-4 text-sm text-foreground-800 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-base text-primary-600" />
                  筛选与排序
                </span>
                <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-arrow-down-s-line" />
                </span>
              </button>
            </div>

            <div className="flex flex-col gap-3 md:gap-4">
              {pageItems.length > 0 ? (
                pageItems.map((item, idx) => <NewsCard key={item.id} news={item} index={idx} />)
              ) : (
                <EmptyState
                  icon="ri-newspaper-line"
                  title="暂无符合条件的资讯"
                  desc="当前筛选条件下没有找到资讯，换个分类或重置筛选条件试试。"
                  onReset={reset}
                />
              )}
            </div>

            {pageItems.length > 0 && (
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onChange={(p) => {
                  update({ page: String(p) });
                  window.scrollTo({ top: 260, behavior: "smooth" });
                }}
              />
            )}
          </div>
        </div>
      </section>

      {filterOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setFilterOpen(false)}
            className="absolute inset-0 bg-foreground-950/60 cursor-pointer"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-lg p-5 pb-8 animate-sheet-up max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-foreground-950 text-base">筛选与排序</h2>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {FilterBar}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  reset();
                  setFilterOpen(false);
                }}
                className="flex-1 h-11 rounded-md border border-background-300 text-foreground-800 text-sm font-medium cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}